import React, { useEffect, useRef, useState } from 'react';
import { DEFAULT_WEBSITE_CONTENT } from '../../config/defaults';
import HomepageAboutEditor from './HomepageAboutEditor';
import {
  fetchPublishedWebsiteContent,
  mergeWebsiteContent,
  readLocalWebsiteContent,
  savePublishedWebsiteContent,
  WebsiteContent,
  WhereIShootLink,
} from '../../utils/websiteContent';
import './HomepageAboutEditor.css';

const ContentManagement: React.FC = () => {
  const [content, setContent] = useState<WebsiteContent>(DEFAULT_WEBSITE_CONTENT);
  const [area, setArea] = useState<'details' | 'about'>('details');
  
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const dirtyRef = useRef(false);

  useEffect(() => {
    const loadWebsiteContent = async () => {
      const local = readLocalWebsiteContent();
      if (local && !dirtyRef.current) setContent(local);
      const remote = await fetchPublishedWebsiteContent();
      if (remote && !dirtyRef.current) setContent(remote);
    };
    loadWebsiteContent();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const existing = readLocalWebsiteContent();
      const remote = await fetchPublishedWebsiteContent();
      const next = mergeWebsiteContent({
        ...(existing || {}),
        ...(remote || {}),
        studioName: content.studioName,
        heroDescription: content.heroDescription,
        aboutDescription: content.aboutDescription,
        contactEmail: content.contactEmail,
        contactPhone: content.contactPhone,
        contactLocation: content.contactLocation,
        footerLocality: content.footerLocality,
        areasServed: content.areasServed,
        reviewLabel: content.reviewLabel,
        reviewUrl: content.reviewUrl,
        whereIShootHeading: content.whereIShootHeading,
        whereIShootBody: content.whereIShootBody,
        whereIShootLinks: content.whereIShootLinks,
      });
      const published = await savePublishedWebsiteContent(next);
      if (!published) {
        setSaveMessage('Error saving content. The homepage still shows the previous version.');
        return;
      }
      setContent(next);
      dirtyRef.current = false;
      setIsDirty(false);
      setSaveMessage('Website content saved. Visitors now see this version.');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving website content:', error);
      setSaveMessage('Error saving content. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: 'studioName' | 'heroDescription' | 'aboutDescription' | 'contactEmail' | 'contactPhone' | 'contactLocation' | 'footerLocality' | 'areasServed' | 'reviewLabel' | 'reviewUrl' | 'whereIShootHeading' | 'whereIShootBody', value: string) => {
    dirtyRef.current = true;
    setContent(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (saveMessage) setSaveMessage('');
  };

  const updateShootLink = (index: number, field: keyof WhereIShootLink, value: string) => {
    dirtyRef.current = true;
    setContent((prev) => ({
      ...prev,
      whereIShootLinks: prev.whereIShootLinks.map((link, linkIndex) => (
        linkIndex === index ? { ...link, [field]: value } : link
      )),
    }));
    setIsDirty(true);
    if (saveMessage) setSaveMessage('');
  };

  const addShootLink = () => {
    if (content.whereIShootLinks.length >= 6) return;
    dirtyRef.current = true;
    setContent((prev) => ({
      ...prev,
      whereIShootLinks: [...prev.whereIShootLinks, { label: '', href: '/galleries/' }],
    }));
    setIsDirty(true);
  };

  const removeShootLink = (index: number) => {
    dirtyRef.current = true;
    setContent((prev) => ({
      ...prev,
      whereIShootLinks: prev.whereIShootLinks.filter((_, linkIndex) => linkIndex !== index),
    }));
    setIsDirty(true);
  };

  return (
    <section className="admin-section">
      <div className="section-header">
        <h2>Homepage text</h2>
        <p>Edit the words used across the site. Homepage About Me is edited separately from the About page.</p>
      </div>

      <div className="website-content-switch" role="navigation" aria-label="Homepage text">
        <button type="button" aria-current={area === 'details' ? 'page' : undefined} onClick={() => setArea('details')}>
          Site details
        </button>
        <button type="button" aria-current={area === 'about' ? 'page' : undefined} onClick={() => setArea('about')}>
          Homepage — About Me
        </button>
      </div>
      
      <div className="section-content">
        {area === 'about' ? (
          <HomepageAboutEditor />
        ) : (
        <form onSubmit={handleSave} className="content-form">
          <div className="form-group">
            <label htmlFor="studio-name">Studio Name</label>
            <input 
              type="text" 
              id="studio-name" 
              value={content.studioName}
              onChange={(e) => handleInputChange('studioName', e.target.value)}
              required 
              className="form-input"
              placeholder="Your photography business name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hero-description">Hero Description</label>
            <textarea 
              id="hero-description" 
              value={content.heroDescription}
              onChange={(e) => handleInputChange('heroDescription', e.target.value)}
              rows={4} 
              required
              className="form-textarea"
              placeholder="Shown on the About page"
            />
            <div className="form-help">
              <small>Shown on the About page, and used in the site details shared with search engines. The homepage introduction is edited under Homepage — About Me.</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="about-description">About Description</label>
            <textarea 
              id="about-description" 
              value={content.aboutDescription}
              onChange={(e) => handleInputChange('aboutDescription', e.target.value)}
              rows={6} 
              required
              className="form-textarea"
              placeholder="Tell visitors more about yourself and your photography"
            />
            <div className="form-help">
              <small>This is the About page introduction. Editing it does not change the homepage About Me section after that section has been saved.</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contact-email">Contact Email</label>
            <input 
              type="email" 
              id="contact-email" 
              value={content.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              required
              className="form-input"
              placeholder="your.email@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-phone">Contact Phone</label>
            <input 
              type="tel" 
              id="contact-phone" 
              value={content.contactPhone}
              onChange={(e) => handleInputChange('contactPhone', e.target.value)}
              className="form-input"
              placeholder="+27 (0) XX XXX XXXX"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-location">Location</label>
            <input 
              type="text" 
              id="contact-location" 
              value={content.contactLocation}
              onChange={(e) => handleInputChange('contactLocation', e.target.value)}
              className="form-input"
              placeholder="Your city, Country"
            />
          </div>

          <h3 className="content-form-heading">Footer</h3>
          <p className="content-form-note">
            Shown at the bottom of every public page. The name, email, and phone come from the fields above.
            The place names are also published as structured data, so keep the areas you want to be found for.
            Galleries, Blog, About, and Contact stay linked.
          </p>

          <div className="form-group">
            <label htmlFor="footer-locality">Based in</label>
            <input
              type="text"
              id="footer-locality"
              value={content.footerLocality}
              onChange={(e) => handleInputChange('footerLocality', e.target.value)}
              className="form-input"
              placeholder="Noordhoek, Cape Town"
            />
          </div>

          <div className="form-group">
            <label htmlFor="areas-served">Areas I cover</label>
            <textarea
              id="areas-served"
              value={content.areasServed}
              onChange={(e) => handleInputChange('areasServed', e.target.value)}
              rows={3}
              className="form-textarea"
              placeholder="Cape Town, Noordhoek, Hout Bay"
            />
            <div className="form-help">
              <small>Separate places with commas. This line is visible in the footer and in the local-business details search engines read.</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="review-label">Review link text</label>
            <input
              type="text"
              id="review-label"
              value={content.reviewLabel}
              onChange={(e) => handleInputChange('reviewLabel', e.target.value)}
              className="form-input"
              placeholder="Leave us a Google review"
            />
          </div>

          <div className="form-group">
            <label htmlFor="review-url">Google review link</label>
            <input
              type="url"
              id="review-url"
              value={content.reviewUrl}
              onChange={(e) => handleInputChange('reviewUrl', e.target.value)}
              className="form-input"
              placeholder="https://g.page/..."
            />
            <div className="form-help">
              <small>Use a Google or g.page review address. Any other address is kept as the current Google review link.</small>
            </div>
          </div>

          <h3 className="content-form-heading">Where I shoot</h3>
          <p className="content-form-note">
            Shown on the homepage and the About page. The paragraph stays as text on the page, with links through to the galleries.
          </p>

          <div className="form-group">
            <label htmlFor="where-i-shoot-heading">Heading</label>
            <input
              type="text"
              id="where-i-shoot-heading"
              value={content.whereIShootHeading}
              onChange={(e) => handleInputChange('whereIShootHeading', e.target.value)}
              className="form-input"
              placeholder="Where I shoot"
            />
          </div>

          <div className="form-group">
            <label htmlFor="where-i-shoot-body">Paragraph</label>
            <textarea
              id="where-i-shoot-body"
              value={content.whereIShootBody}
              onChange={(e) => handleInputChange('whereIShootBody', e.target.value)}
              rows={5}
              className="form-textarea"
            />
          </div>

          <div className="form-group">
            <label>Gallery links</label>
            <div className="where-i-shoot-links">
              {content.whereIShootLinks.map((link, index) => (
                <div className="where-i-shoot-link" key={index}>
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) => updateShootLink(index, 'label', e.target.value)}
                    className="form-input"
                    placeholder="Link text"
                    aria-label={`Gallery link text ${index + 1}`}
                  />
                  <input
                    type="text"
                    value={link.href}
                    onChange={(e) => updateShootLink(index, 'href', e.target.value)}
                    className="form-input"
                    placeholder="/galleries/family"
                    aria-label={`Gallery link path ${index + 1}`}
                  />
                  <button type="button" className="where-i-shoot-remove" onClick={() => removeShootLink(index)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
            {content.whereIShootLinks.length < 6 && (
              <button type="button" className="where-i-shoot-add" onClick={addShootLink}>
                Add gallery link
              </button>
            )}
            <div className="form-help">
              <small>Use a path on this site, such as /galleries/family. The links are written into the page so search engines can follow them.</small>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className={`btn btn-primary ${!isDirty ? 'btn-disabled' : ''}`}
              disabled={!isDirty || saving}
            >
              <i className="fas fa-save"></i>
              {saving ? 'Saving…' : 'Save Content'}
            </button>
            
            {saveMessage && (
              <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
                {saveMessage}
              </div>
            )}
          </div>
        </form>
        )}
      </div>
    </section>
  );
};

export default ContentManagement;
